import React, {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import {v4 as uuid} from 'uuid';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import {IElementQuestionMatchPair, MatchPairQuestionMode} from '../../../../incase-domain/elements/question/questionMatchPair';
import {shuffleElementsOrder} from '../../../../utils/utils';
import {generateErrorMessages, getErrorClasses} from '../../../../utils/validation';
import ElementShadow from '../common/elementShadow';
import {COLOR_FIELDS} from '../common/fontAndBackgroundColorPicker';
import AdvancedOptions from './components/advancedOptions/AdvancedOptions';
import QuestionOptions, {QUESTION_OPTIONS} from './components/QuestionOptions';
import HighlightedChangeElementOrder from './matchPair/HighlightedChangeElementOrder';
import HighlightedPairsElement from './matchPair/HighlightedPairsElement';
import MatchPairModeSelect from './matchPair/MatchPairModeSelect';
import {TABS} from '../../form/part/tabs';
import SectionTitle from '../../../elements/common/SectionTitle';
import QuestionText from './components/QustionText';
import Button from '../../../../incase-domain/components/button/Button';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';
import AnimationForm from '../common/animationForm';

interface HighlightedQuestionMatchPairElementComponentProps {
    errors: IValidationErrorObject | null;
    activeTab: string;
}

function HighlightedQuestionMatchPairElementComponent(props: HighlightedQuestionMatchPairElementComponentProps): ReactElement {
    const {errors, activeTab} = props;
    const {t} = useTranslation();
    const highlightedElement = useHighlightedElement() as IElementQuestionMatchPair;
    const updateHighlightedElement = useSetHighlightedElement();

    function addElement(
        highlightedElement: IElementQuestionMatchPair,
        highlightedElementChangeHandler: {
            multiple: (fields: (keyof IElementQuestionMatchPair)[], values: unknown[]) => void;
        }
    ): void {
        const elementAId = uuid();
        const elementBId = uuid();
        const newAnswersA = [
            ...highlightedElement.leftElement,
            {id: elementAId, text: `Element A${highlightedElement.leftElement.length + 1}`},
        ];
        const newAnswersB = [
            ...highlightedElement.rightElement,
            {id: elementBId, text: `Element B${highlightedElement.rightElement.length + 1}`},
        ];
        const newPairs = [
            ...highlightedElement.pairs,
            {
                leftElementId: elementAId,
                rightElementId: elementBId,
            },
        ];
        highlightedElementChangeHandler.multiple(['leftElement', 'rightElement', 'pairs'], [newAnswersA, newAnswersB, newPairs]);
    }

    function swapAnswersOrderHandler(highlightedElementChangeHandler: {
        multiple: (fields: (keyof IElementQuestionMatchPair)[], values: unknown[]) => void;
    }): void {
        const newLeftOrder = shuffleElementsOrder(highlightedElement.leftElement);
        const newRightOrder = shuffleElementsOrder(highlightedElement.rightElement);

        highlightedElementChangeHandler.multiple(['leftElement', 'rightElement'], [newLeftOrder, newRightOrder]);
    }

    if (activeTab === TABS.STYLE.id) {
        return (
            <>
                <AdvancedOptions
                    highlightedElement={highlightedElement}
                    highlightedElementChangeHandler={updateHighlightedElement}
                    answerColorFields={[
                        COLOR_FIELDS.CORRECT_COLOR,
                        COLOR_FIELDS.INCORRECT_COLOR,
                        ...(highlightedElement.matchPairMode === MatchPairQuestionMode.CARDS ? [COLOR_FIELDS.SELECTED_COLOR] : []),
                    ]}
                />
                <ElementShadow highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
            </>
        );
    }

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <QuestionOptions
                    highlightedElement={highlightedElement}
                    highlightedElementChangeHandler={updateHighlightedElement}
                    questionOptions={[
                        QUESTION_OPTIONS.BLOCKING_QUESTION,
                        QUESTION_OPTIONS.HAVE_TRY_BUTTON,
                        QUESTION_OPTIONS.SHOW_IN_REPORT,
                        QUESTION_OPTIONS.RETRY_QUESTION,
                    ]}
                />
                <AnimationForm
                    appearAfter={highlightedElement.appearAfter || 0}
                    elementAnimations={highlightedElement.elementAnimations}
                    highlightedElementChangeHandler={updateHighlightedElement}
                />
            </>
        );
    }

    return (
        <>
            <QuestionText
                highlightedElement={highlightedElement}
                errors={errors}
                highlightedElementChangeHandler={updateHighlightedElement}
            />

            <MatchPairModeSelect highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />

            <div className="l-group--form">
                <div className={`l-group__item ${getErrorClasses(errors, 'leftElement')} ${getErrorClasses(errors, 'rightElement')}`}>
                    <SectionTitle
                        label={t('presEditPage.editSidebar.elements.matchPairQuestion.correctPairs')}
                        leading={
                            <Button
                                className="c-sequenceQuestionElementOrder__shuffleButton"
                                size="2xs"
                                variant="ghost"
                                color="primary"
                                onClick={() => addElement(highlightedElement, updateHighlightedElement)}
                                leading="ph:plus-bold"
                            >
                                {t('designSidepanel.new')}
                            </Button>
                        }
                    />

                    {generateErrorMessages(errors, 'leftElement')}
                    {generateErrorMessages(errors, 'rightElement')}

                    <HighlightedPairsElement
                        highlightedElement={highlightedElement}
                        errors={errors}
                        highlightedElementChangeHandler={updateHighlightedElement}
                    />
                </div>
            </div>

            <div className="l-group--form">
                <div className="u-flex u-h-between u-v-center">
                    <SectionTitle label={t('presEditPage.editSidebar.elements.matchPairQuestion.order')} />
                    <Button
                        className="c-matchPairQuestionElement__addPairButton"
                        size="2xs"
                        variant="ghost"
                        onClick={() => {
                            swapAnswersOrderHandler(updateHighlightedElement);
                        }}
                    >
                        {t('presEditPage.editSidebar.elements.matchPairQuestion.shuffle')}
                    </Button>
                </div>
                <div style={{display: 'flex', gap: '0.5rem'}}>
                    <div style={{flex: '1', width: '50%'}}>
                        <HighlightedChangeElementOrder
                            highlightedElementArrayToDisplay={highlightedElement.leftElement}
                            highlightedElementChangeHandler={updateHighlightedElement}
                            highlightedElementsToChangeOrder="leftElement"
                        />
                    </div>
                    <div style={{flex: '1', width: '50%'}}>
                        <HighlightedChangeElementOrder
                            highlightedElementArrayToDisplay={highlightedElement.rightElement}
                            highlightedElementChangeHandler={updateHighlightedElement}
                            highlightedElementsToChangeOrder="rightElement"
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default HighlightedQuestionMatchPairElementComponent;
